import {
  TimelogUserTotal,
  useGetTimelogsByUser,
} from '@/api/queries/timelog/useGetTimelogsByUser';
import { humanizeCurrency } from '@/helpers/currency';
import { useUserHasPermission } from '@/hooks/usePermissions';
import { ManageableItemsList, UserAvatar, Text } from '@payaca/ambrosia';
import { TimelogsPermissions } from '@payaca/permissions/timelogs/timelogs.permissions';
import { parse, toSeconds } from 'iso8601-duration';
import { FC } from 'react';
import { isoDurationToReadableString } from './ListedTimelogs';
import { useListedTimelogsContext } from './listedTimelogsContext';

type UserTimelogs = TimelogUserTotal['timelogs'];
type Row = {
  id: string;
  user: TimelogUserTotal;
  entryCount: UserTimelogs['totalCount'];
  totalDuration: UserTimelogs['totalDuration'];
  totalCost: UserTimelogs['totalCost'];
};

const durationToSeconds = (iso?: string | null) => {
  if (!iso) return 0;
  try {
    return toSeconds(parse(iso));
  } catch {
    return 0;
  }
};

/**
 * Per-user summary for the current filter/period (a payroll-style view). Each
 * account user's totals come from the User.timelogs field; we show only those
 * who logged time in the period, most hours first. Clicking a user drills into
 * their entries in the list view.
 */
export const TimelogsByUserTable: FC = () => {
  const { queryInput, setFilter, setViewMode, setSelectedTeamIds } =
    useListedTimelogsContext();
  const { data, isLoading } = useGetTimelogsByUser(queryInput);

  const hasCostPermission = useUserHasPermission({
    permissions: [TimelogsPermissions.GET_TIMELOG_COST_PER_HOUR],
  });

  // Each row's totals are already scoped to its own user, so we apply the
  // assignee filter here by listing only the selected users (empty = everyone).
  const selectedUserIds =
    queryInput?.assignees?.filter((a) => a.type === 'USER').map((a) => a.id) ??
    [];

  const items: Row[] = (data?.me.user.account.users ?? [])
    .filter(
      (u) =>
        selectedUserIds.length === 0 || selectedUserIds.includes(u.publicId)
    )
    .filter((u) => u.timelogs.totalCount > 0)
    .toSorted(
      (a, b) =>
        durationToSeconds(b.timelogs.totalDuration) -
        durationToSeconds(a.timelogs.totalDuration)
    )
    .map((u) => ({
      id: u.publicId,
      user: u,
      entryCount: u.timelogs.totalCount,
      totalDuration: u.timelogs.totalDuration,
      totalCost: u.timelogs.totalCost,
    }));

  const drillIntoUser = (row: Row) => {
    // Isolate this one user: clear any team filter (it would re-broaden the
    // list via team expansion) and set the assignee to their publicId (the
    // filter is keyed on publicId — see AssigneeFilter / AccountUserAvatarSelector).
    setSelectedTeamIds([]);
    setFilter({ assignees: [{ id: row.user.publicId, type: 'USER' }] });
    setViewMode('list');
  };

  return (
    <ManageableItemsList.Table
      emptyText="No timelogs match these filters"
      uniqueKey="id"
      isLoading={isLoading}
      items={items}
      onClickRow={drillIntoUser}
    >
      <ManageableItemsList.Table.Column<Row, 'user'>
        header="User"
        field="user"
        render={(user) => (
          <div className="flex items-center gap-2">
            <UserAvatar
              sizeVariant="xs"
              borderColourVariant="white"
              user={{
                firstName: user.firstName,
                lastName: user.lastName,
                emailAddress: user.email,
                userColour: user.colour,
                imgSrc: user.avatarUrl,
              }}
            />
            <span className="whitespace-nowrap normal-case">
              <Text as="span">{user.fullName}</Text>
            </span>
          </div>
        )}
      />
      <ManageableItemsList.Table.Column<Row, 'entryCount'>
        header="Entries"
        field="entryCount"
        render={(entryCount) => entryCount}
      />
      <ManageableItemsList.Table.Column<Row, 'totalDuration'>
        header="Total time"
        field="totalDuration"
        render={(totalDuration) =>
          isoDurationToReadableString(totalDuration || 'PT0S')
        }
      />
      {hasCostPermission && (
        <ManageableItemsList.Table.Column<Row, 'totalCost'>
          header="Total cost"
          field="totalCost"
          align="end"
          render={(totalCost) =>
            totalCost ? humanizeCurrency(totalCost) : null
          }
        />
      )}
    </ManageableItemsList.Table>
  );
};
